// T-017: toast "celebrativo" per le aggiunte (creazioni / salvataggi). Si
// renderizza al centro dello schermo, con un box gradient e sparkles animati,
// auto-dismiss e una vibrazione brevissima. È volutamente "non invasivo":
// nessun overlay, nessun blocco dell'input, e durata ~1.8s.
//
// Per gli errori NON si usa questo: vanno mostrati con IonToast rosso in
// basso, così l'utente capisce subito che è un problema, non una conferma.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

const STYLE_ID = 'trackeat-celebration-styles';

// Inietta una volta sola le keyframes globali. Le rimettiamo inline così non
// servono file CSS esterni e la cosa resta autonoma.
function ensureStylesInjected(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
@keyframes trackeat-pop-in {
  0%   { opacity: 0; transform: scale(0.6) translateY(-8px); }
  55%  { opacity: 1; transform: scale(1.08) translateY(0); }
  100% { opacity: 1; transform: scale(1.0) translateY(0); }
}
@keyframes trackeat-pop-out {
  0%   { opacity: 1; transform: scale(1.0); }
  100% { opacity: 0; transform: scale(0.92); }
}
@keyframes trackeat-sparkle {
  0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.2); }
  35%  { opacity: 1; }
  100% { opacity: 0; transform: translate(var(--tx), var(--ty)) scale(1); }
}
`;
  document.head.appendChild(style);
}

// Vibrazione cortese: solo se l'API è disponibile (PWA / Android WebView),
// silente su iOS (che non implementa navigator.vibrate). 35ms = single tick.
function gentleVibrate(): void {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(35);
    }
  } catch {
    // noop
  }
}

interface CelebrationState {
  id: number;
  message: string;
  phase: 'in' | 'out';
}

interface CelebrationContextValue {
  celebrate: (message: string) => void;
}

const CelebrationContext = createContext<CelebrationContextValue | null>(null);

export function CelebrationProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<CelebrationState | null>(null);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const removeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    ensureStylesInjected();
    return () => {
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
      if (removeTimer.current) clearTimeout(removeTimer.current);
    };
  }, []);

  const celebrate = useCallback((message: string) => {
    if (dismissTimer.current) clearTimeout(dismissTimer.current);
    if (removeTimer.current) clearTimeout(removeTimer.current);
    const id = Date.now();
    setState({ id, message, phase: 'in' });
    gentleVibrate();
    // dopo ~1.4s avvia la fase di uscita (200ms), poi rimuove dal DOM
    dismissTimer.current = setTimeout(() => {
      setState((prev) => (prev && prev.id === id ? { ...prev, phase: 'out' } : prev));
      removeTimer.current = setTimeout(() => {
        setState((prev) => (prev && prev.id === id ? null : prev));
      }, 200);
    }, 1400);
  }, []);

  const value = useMemo<CelebrationContextValue>(() => ({ celebrate }), [celebrate]);

  return (
    <CelebrationContext.Provider value={value}>
      {children}
      {state && (
        <div
          aria-live="polite"
          role="status"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 99999,
          }}
        >
          <div
            style={{
              position: 'relative',
              padding: '16px 24px',
              borderRadius: 18,
              background: 'linear-gradient(135deg, #0066cc, #8b5cf6)',
              color: '#fff',
              fontSize: 16,
              fontWeight: 700,
              letterSpacing: 0.2,
              boxShadow: '0 12px 32px rgba(102, 102, 204, 0.45), 0 2px 6px rgba(15, 23, 42, 0.2)',
              animation:
                state.phase === 'in'
                  ? 'trackeat-pop-in 320ms cubic-bezier(0.34, 1.56, 0.64, 1) both'
                  : 'trackeat-pop-out 200ms ease-out both',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              maxWidth: '80vw',
              textAlign: 'center',
              whiteSpace: 'pre-line',
            }}
          >
            <span style={{ fontSize: 22, lineHeight: 1 }}>✨</span>
            <span>{state.message}</span>

            {/* Sparkles: 6 stelline che esplodono in radiale dal centro del box.
                Ogni stellina ha un offset finale (tx, ty) calcolato come CSS var. */}
            {state.phase === 'in' &&
              SPARKLE_OFFSETS.map((off, i) => (
                <span
                  key={i}
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: 18,
                    color: '#fde68a',
                    pointerEvents: 'none',
                    // @ts-expect-error custom CSS vars are valid at runtime
                    '--tx': off.tx,
                    '--ty': off.ty,
                    animation: `trackeat-sparkle 700ms ease-out ${i * 35}ms both`,
                  }}
                >
                  {off.glyph}
                </span>
              ))}
          </div>
        </div>
      )}
    </CelebrationContext.Provider>
  );
}

const SPARKLE_OFFSETS: { tx: string; ty: string; glyph: string }[] = [
  { tx: 'calc(-50% - 70px)', ty: 'calc(-50% - 30px)', glyph: '✦' },
  { tx: 'calc(-50% + 70px)', ty: 'calc(-50% - 30px)', glyph: '✦' },
  { tx: 'calc(-50% - 60px)', ty: 'calc(-50% + 40px)', glyph: '✧' },
  { tx: 'calc(-50% + 60px)', ty: 'calc(-50% + 40px)', glyph: '✧' },
  { tx: 'calc(-50% - 0px)',  ty: 'calc(-50% - 55px)', glyph: '⭐' },
  { tx: 'calc(-50% + 0px)',  ty: 'calc(-50% + 55px)', glyph: '⭐' },
];

export function useCelebrate(): (message: string) => void {
  const ctx = useContext(CelebrationContext);
  if (!ctx) {
    // Fallback no-op: utile se per qualche motivo il provider non è stato
    // montato (es. test isolato). Non vogliamo crashare il chiamante.
    return () => undefined;
  }
  return ctx.celebrate;
}
